// Admin/login routes are meant to live on this deployment's bare base
// domain (lunchday.app in production, staging.lunchday.app on staging) —
// never on a school's subdomain (e.g. lhms.lunchday.app) — so that logging
// in doesn't tie the admin's session to whichever school subdomain happened
// to be open, and a stored auth token isn't scattered per-school-origin
// across localStorage. See AGENTS.md's "Schools are database-backed"
// section for the subdomain scheme this is undoing, and
// backend/env.CanonicalBaseDomainVar for the server-side equivalent of
// "which one is mine" this mirrors. Only these two domains actually exist
// (see BASE_DOMAINS in fly.toml / fly.staging.toml) — there's no per-school
// or per-env config to read on the client, so they're just listed here.
const CANONICAL_BASE_DOMAINS = ['staging.lunchday.app', 'lunchday.app'];

// Returns this deployment's canonical base domain if the current hostname
// is that domain or a subdomain of it (a school's subdomain, e.g.
// "lhms.lunchday.app"), or null if the host doesn't match either known
// scheme at all (local dev on localhost, an unrelated preview host, etc.)
// — in which case there's nothing to redirect to.
export function canonicalBaseDomainForCurrentHost() {
  const host = window.location.hostname;
  for (const base of CANONICAL_BASE_DOMAINS) {
    if (host === base || host.endsWith(`.${base}`)) {
      return base;
    }
  }
  return null;
}

// True if the current host is a school subdomain of a known base domain
// (e.g. "lhms.lunchday.app") and so needs to be bounced to the bare base
// domain. False both when already on the bare base domain and when the
// host doesn't match a known base-domain family at all (local dev on
// localhost, an unrelated preview host) — there's nothing to redirect to
// either way, so callers can treat both as "safe to render normally".
export function needsCanonicalRedirect() {
  const base = canonicalBaseDomainForCurrentHost();
  return base !== null && window.location.hostname !== base;
}

// Redirects the browser to the same path+query on the canonical base
// domain, preserving protocol and port (port matters for local testing
// against a built/served bundle). No-op if already there, or if the host
// doesn't match a known base domain scheme (e.g. local dev).
export function redirectToCanonicalBaseDomain() {
  if (!needsCanonicalRedirect()) {
    return;
  }
  const base = canonicalBaseDomainForCurrentHost();
  const { protocol, port, pathname, search, hash } = window.location;
  window.location.replace(`${protocol}//${base}${port ? `:${port}` : ''}${pathname}${search}${hash}`);
}
